// cSpell:enableCompoundWords
import { ASComponentSize, ASLoader, ComponentSize } from '@eleventheye/asui';
import { appLoader } from 'api/UserAPI';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis } from 'app/constants';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  getApplicationInitialized,
  getIsIndexDBInitialized,
  initializeWebWorkerDBAuditor,
  setApplicationInitialized,
  setIndexDBInitialized,
  setShowLoader,
} from 'app/slices/AppUiSlice';
import { setAppInitialized } from 'app/slices/NavigationSlice';
import { getBESyncSeasons } from 'app/slices/SyncManagerSlice';
import { getShowLoggedInElements, getUserAuthState, UserAuthState } from 'app/slices/UserSlice';
import AppHeader from 'components/appheader';
import NavigationManager from 'components/navigationmanager';
import NotFound from 'components/notfound/NotFound';
import useLoader from 'hooks/useLoader';
import useViewportDimensions from 'hooks/useViewportDimensions';
import { analytics, logEvent } from 'lib/firebase';
import { initDBVersion } from 'lib/idb/IndexDb';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { getNavigationTarget } from 'routes/navigation';

import styles from './App.module.scss';

const StatsManager = lazy(() => import('app/scenes/statsmanager'));
const UserSettings = lazy(() => import('components/modalpanels/usersettings/UserSettings'));

const CONSOLE_ON = getConsoleState(ConsoleID.App);

export type UserStatus = {
  success: boolean;
};

export const App: React.FC = () => {
  const isWebWorkerInitializedRef = useRef<boolean>(false);
  const initStartedRef = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const { isLoaderOpen, size, type, isModal } = useLoader();

  // const isStarterPanelDisplayed = useAppSelector(getShowStarterScreen);
  // const savedRound = useAppSelector(getRouteToLoadAfterAuth);
  const userAuthState = useAppSelector(getUserAuthState);
  const { viewportDimensions } = useViewportDimensions();
  const { width } = viewportDimensions;
  const isDBInitialized = useAppSelector(getIsIndexDBInitialized);
  const isApplicationInitialized = useAppSelector(getApplicationInitialized);

  const [userStatus, setUserStatus] = useState<UserStatus | undefined>(undefined);
  const [componentSize, setComponentSize] = useState<ASComponentSize>(ComponentSize.Large);
  // const [userState, setUserState] = useState<UserAuthState>(authState);

  const location = useLocation();
  const seasons = useAppSelector(getBESyncSeasons);
  const target = getNavigationTarget(
    location.pathname,
    userAuthState === UserAuthState.LoggedIn && seasons.length ? seasons.map(({ seasonID }) => seasonID) : undefined,
  );
  const showLoggedInElements = useAppSelector(getShowLoggedInElements);

  const initializeDB = () => {
    console.log(`${Emojis.rocket} %c APP initialized DB... %c`, Cls.DrkGn, '');
    dispatch(setIndexDBInitialized(true));
  };

  useEffect(() => {
    console.log(`${Emojis.rocketInit} %c APP initializing DB... %c`, Cls.Pale, '');
    initDBVersion(initializeDB);
  }, []);

  const loadStatus = async () => {
    if (!initStartedRef.current) {
      initStartedRef.current = true;
      const status = (await appLoader(dispatch)) as { success: boolean } | undefined;
      setUserStatus(status);
      console.log(`${Emojis.rocket} %c APP finished loading... %c`, Cls.DrkGn, '');

      return status;
    }
  };

  useEffect(() => {
    console.log(`${Emojis.rocketInit} %c APP initializing... ${userStatus?.success} %c`, Cls.Pale, '');
    if (userStatus?.success) {
      dispatch(setShowLoader({ visible: false }));
      dispatch(setApplicationInitialized(true));
      logEvent(analytics, 'app_status', { isIntialized: true, componentSize: componentSize });
    }
  }, [userStatus]);

  // APP INITIALIZATION
  if (!initStartedRef.current) {
    dispatch(setShowLoader({ visible: true }));
    loadStatus();
  }

  useEffect(() => {
    if (!isDBInitialized) return;

    if (!isWebWorkerInitializedRef.current && isDBInitialized) {
      dispatch(initializeWebWorkerDBAuditor());
      isWebWorkerInitializedRef.current = true;
    }
  }, [isDBInitialized]);

  // const updateRouteIfNotSet = useCallback(() => {
  //   const cPath = window.location.pathname;
  //   const isAuthPath = cPath === '/auth' || cPath === '/auth/' || cPath.startsWith('/auth/');
  //   const isSavedRoundNotAHappyPath = !!savedRound && (savedRound.startsWith('/auth') || savedRound === '/');
  //   const pathMsg = `isAuthPath %c ${isAuthPath} %c isSavedRoundNotAHappyPath %c ${isSavedRoundNotAHappyPath} %c`;
  //   const msg = `${Emojis.eyes} Current path %c ${cPath} %c savedRound %c ${savedRound} %c ${pathMsg}`;
  //   if (CONSOLE_ON) console.log(msg, Cls.Loc, '', Cls.Val, '', Cls.Val, '', Cls.Val, '');
  //   if (isAuthPath || cPath === '/') {
  //     if (isSavedRoundNotAHappyPath) {
  //       if (CONSOLE_ON) console.log(`${Emojis.eyes} %c Not redirecting to auth path %c`, Cls.Info, '');
  //       navigate(AppRoutes.WeeklyGames + '/2025/week1', { replace: true });
  //     } else {
  //       if (CONSOLE_ON)
  //         console.log(`${Emojis.eyes} %c Redirecting to savedRound %c ${savedRound} %c`, Cls.Info, '', Cls.Val, '');
  //       navigate(savedRound, { replace: true });
  //     }
  //   }
  // }, [savedRound]);

  useEffect(() => {
    if (!isDBInitialized) return;
    if (userAuthState === UserAuthState.Authenticating) return;
    const log = `authState %c ${userAuthState} %c userStatus?.success %c ${userStatus?.success} %c`;
    console.log(`${Emojis.eyes} ${log}`, Cls.Val, '', Cls.Val, '');
    dispatch(setAppInitialized(true));
    dispatch(setApplicationInitialized(true));
    logEvent(analytics, 'app_status', { isIntialized: true, componentSize: componentSize });
    if (userAuthState === UserAuthState.LoggedIn) {
      if (CONSOLE_ON) console.log(`${Emojis.eyes} %c Logged in %c`, Cls.Army, '');
      dispatch(setShowLoader({ visible: false }));
      dispatch(setAppInitialized(isApplicationInitialized));
    } else if (userAuthState === UserAuthState.LoggedOut || userAuthState === UserAuthState.NotLoggedIn) {
      if (CONSOLE_ON) console.log(`${Emojis.eyes} %c Not logged in %c`, Cls.Cri, '');
      dispatch(setShowLoader({ visible: false }));
    } else {
      if (CONSOLE_ON) console.log(`${Emojis.eyes} %c No user status yet... %c`, Cls.Info, '');
    }
  }, [userStatus?.success, userAuthState, isDBInitialized, componentSize, isApplicationInitialized]);

  // useEffect(() => {
  //   if (!isStarterPanelDisplayed) {
  //     if (savedRound !== '/') {
  //       if (CONSOLE_ON) console.log(`${Emojis.eyes} %c Should navigate to ${savedRound} %c`, Cls.Info, '');
  //     } else {
  //       if (CONSOLE_ON) console.log(`${Emojis.eyes} %c Should I redirect? %c`, Cls.Info, '');
  //     }
  //   }
  // }, [isStarterPanelDisplayed, savedRound]);

  useEffect(() => {
    if (width < 475) {
      setComponentSize(ComponentSize.Small);
    } else if (width < 768) {
      setComponentSize(ComponentSize.Medium);
    } else {
      setComponentSize(ComponentSize.Large);
    }
  }, [width]);

  // const showAuthentication = useMemo(() => {
  //   return (
  //     userAuthState === UserAuthState.LoggedOut ||
  //     userAuthState === UserAuthState.NotLoggedIn ||
  //     userAuthState === UserAuthState.Error
  //   );
  // }, [userAuthState, userStatus?.success]);

  // const showApp = useMemo(() => {
  //   return userAuthState === UserAuthState.LoggedIn && userStatus?.success && !showAuthentication;
  // }, [userAuthState, userStatus, showAuthentication]);

  const renderScene = (): React.ReactElement => {
    if (target.kind === 'not-found') return <NotFound />;
    if (target.kind === 'public') return <Outlet />;
    if (showLoggedInElements) return <StatsManager />;
    return <ASLoader showLoader={true} loaderType={type} size={size} />;
  };

  return (
    <div id="NFLStats" className={styles.App}>
      <NavigationManager userStatus={userStatus} />
      <AppHeader />
      <main className={styles.AppMain}>
        <Suspense fallback={<ASLoader loaderType={type} size={size} isModal={isModal} />}>{renderScene()}</Suspense>
      </main>
      {showLoggedInElements && (
        <Suspense fallback={null}>
          <UserSettings />
        </Suspense>
      )}
      {/* {loader} */}
      <ASLoader isModal={isModal} loaderType={type} showLoader={isLoaderOpen} size={size} />
    </div>
  );
};

export default App;
